import { PeriodKpis } from "@cryptors/api-client";
import { Stack, Paper, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { KpiCard } from "./KpiCard";

const fmt = (n: number) => new Intl.NumberFormat("fr-FR").format(n);

interface Props {
  data: PeriodKpis;
  // "top5" shows the 5 highest; "all" shows the full ranking (client-side).
  topMode: "top5" | "all";
  showEmailConfirmed: boolean;
}

const RankingBarChart = ({
  title,
  rows,
  color,
}: {
  title: string;
  rows: Array<{ name: string; captures: number }>;
  color: string;
}) => (
  <Paper sx={{ p: 2, minWidth: 380, flex: 1 }} elevation={1}>
    <Typography variant="caption" color="text.secondary">
      {title}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary" mt={2}>
        Aucune donnée
      </Typography>
    ) : (
      <ResponsiveContainer
        width="100%"
        height={Math.max(180, rows.length * 50)}
      >
        <BarChart
          data={rows}
          layout="vertical"
          margin={{ top: 16, right: 32, left: 16, bottom: 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
          <XAxis type="number" />
          <YAxis type="category" dataKey="name" width={140} />
          <Tooltip formatter={(v) => fmt(Number(v))} />
          <Bar dataKey="captures" fill={color} radius={[0, 4, 4, 0]}>
            <LabelList
              dataKey="captures"
              position="right"
              formatter={(v) => fmt(Number(v))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )}
  </Paper>
);

export const AttractivitySection = ({
  data,
  topMode,
  showEmailConfirmed,
}: Props) => {
  const limit = topMode === "top5" ? 5 : undefined;
  const topCollections = data.topCollections.slice(0, limit);
  const topCryptors = data.topCryptors.slice(0, limit);

  return (
    <Stack gap={2}>
      <Typography variant="h6">Chiffres clés</Typography>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <KpiCard title="Joueurs" value={fmt(data.players.total)} />
        {data.scans.total !== null && (
          <KpiCard title="Scans de QR codes" value={fmt(data.scans.total)} />
        )}
        <KpiCard title="Captures d'œuvres" value={fmt(data.captures.total)} />
        {showEmailConfirmed && (
          <KpiCard
            title="Comptes confirmés"
            value={fmt(data.emailConfirmed.total)}
          />
        )}
      </Stack>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <RankingBarChart
          title="Captures par collection"
          rows={topCollections}
          color="#1565c0"
        />
        <RankingBarChart
          title="Captures par œuvre"
          rows={topCryptors.map((c) => ({
            name: `${c.name} (#${c.id})`,
            captures: c.captures,
          }))}
          color="#388e3c"
        />
      </Stack>
    </Stack>
  );
};
